import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button as ShadcnButton } from '@dashboard/components/ui/button';

const FormSubmit = ({
  children,
  isInline,
  align = 'start',
  direction = 'row',
  hasMargin = true,
}) => {
  return (
    <div
      className={clsx('flex items-baseline gap-4', {
        'flex-row justify-between': isInline,
        'justify-start': align === 'start',
        'justify-end': align === 'end',
        'flex-col': direction === 'column',
        'flex-row': direction === 'row',
        'mt-4': hasMargin,
      })}
    >
      {children}
    </div>
  );
};

const FORM_SUBMIT_STATES = {
  INITIAL: 'initial',
  SUBMITTING: 'submitting',
  SUBMITTED: 'submitted',
};

// Map legacy `appearance` prop to shadcn Button variants.
function appearanceToVariant(appearance) {
  switch (appearance) {
    case 'secondary':
    case 'light':
      return 'outline';
    case 'subtle':
      return 'secondary';
    case 'link':
      return 'link';
    case 'primary':
    default:
      return 'default';
  }
}

// Map legacy `size` prop to shadcn Button sizes plus extra full-width sizing.
function sizeToShadcn(size) {
  switch (size) {
    case 'xs':
      return 'xs';
    case 'small':
      return 'sm';
    case 'lg':
      return 'lg';
    case 'full':
      return 'xl';
    case 'base':
    default:
      return 'default';
  }
}

// initial, submitting, submitted, error
const FormSubmitButton = ({
  children,
  appearance = 'primary',
  size = 'base',
  onClick,
  type = 'submit',
  submitSpinner = true,
  disabled = false,
  className: extraClassName,
  ...rest
}) => {
  const { errors, isSubmitting, isValid, isValidating } = useFormikContext();
  const [state, setState] = useState(FORM_SUBMIT_STATES.INITIAL);

  useEffect(() => {
    if (state === FORM_SUBMIT_STATES.INITIAL && isSubmitting && isValid) {
      return setState(FORM_SUBMIT_STATES.SUBMITTING);
    }

    if (state === FORM_SUBMIT_STATES.SUBMITTING && !isSubmitting && isEmpty(errors)) {
      setState(FORM_SUBMIT_STATES.SUBMITTED);

      window.setTimeout(() => {
        setState(FORM_SUBMIT_STATES.INITIAL);
      }, 2000);

      return;
    }

    if (state === FORM_SUBMIT_STATES.SUBMITTING && !isSubmitting) {
      setState(FORM_SUBMIT_STATES.INITIAL);
    }
  }, [errors, isSubmitting, isValidating, state]);

  const isDisabled = disabled || state === FORM_SUBMIT_STATES.SUBMITTING || isValidating;
  const isFullWidth = size === 'full';

  // Drop the legacy `button` prop if a caller still passes it.
  const { button: _legacyButton, ...buttonProps } = rest;

  return (
    <ShadcnButton
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      variant={appearanceToVariant(appearance)}
      size={sizeToShadcn(size)}
      className={clsx({ 'w-full': isFullWidth }, extraClassName)}
      {...buttonProps}
    >
      {children}
      {state === FORM_SUBMIT_STATES.SUBMITTING && submitSpinner && (
        <Loader2 className="ml-2 h-4 w-4 animate-spin" />
      )}
    </ShadcnButton>
  );
};

FormSubmit.Button = FormSubmitButton;

const FormSubmitState = ({ visible }) => {
  if (!visible) return '';

  return <div className="text-sm text-muted-foreground">Changes not saved</div>;
};

FormSubmit.State = FormSubmitState;

const FormSubmitCancel = ({
  disabled = false,
  onClick,
  appearance = 'secondary',
  size = 'base',
  children = 'Cancel',
  className,
  ...rest
}) => {
  return (
    <ShadcnButton
      type="button"
      disabled={disabled}
      variant={appearanceToVariant(appearance)}
      size={sizeToShadcn(size)}
      onClick={onClick}
      className={className}
      {...rest}
    >
      {children}
    </ShadcnButton>
  );
};

FormSubmit.Cancel = FormSubmitCancel;

export default FormSubmit;
